import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { DMA_SOURCE_ID } from '@/hooks/useDistrictMeterAreasLayer';

/**
 * Highlights the District Meter Area under the cursor by toggling the MapLibre
 * `hover` feature-state on the `orbit-dma-source` source. The DMA fill/outline
 * layers brighten/thicken via `['feature-state', 'hover']` expressions in their
 * paint (see useDistrictMeterAreasLayer). The source sets `promoteId: 'ident'`,
 * so each feature's id is its DMA ident.
 *
 * Uses `mousemove` (not `mouseenter`) on the fill layer so `queryRenderedFeatures`
 * returns the topmost DMA when polygons overlap; a ref guards against redundant
 * state writes while the cursor stays over the same DMA.
 *
 * Purely visual — does not select the DMA or change the cursor. Its delegated layer
 * listeners are registered once and survive base-map switches without re-attachment.
 *
 * Safe when the map is not ready, the source/layer does not exist, or feature
 * state operations throw (all guarded).
 */
export function useDmaInteractions(mapRef: RefObject<MapRef | null>, isMapLoaded: boolean): void {
    const hoveredIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const clearHover = () => {
            if (hoveredIdRef.current === null) return;
            try {
                // Set hover back to false rather than removeFeatureState — removeFeatureState can
                // crash MapLibre's render (coalesceChanges); the paint reads false the same as unset.
                mlMap.setFeatureState({ source: DMA_SOURCE_ID, id: hoveredIdRef.current }, { hover: false });
            } catch {
                // Source may have been removed during a style reload — ignore.
            }
            hoveredIdRef.current = null;
        };

        const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
            // No hover feature-state while the camera moves — a highlight created
            // mid-drag/zoom/fly would mark a polygon sliding out from under the cursor.
            if (mlMap.isMoving()) return;
            const feature = e.features?.[0];
            const id = feature?.id;
            if (id == null) return;

            const ident = String(id);
            // No state write while the cursor stays over the same DMA.
            if (ident === hoveredIdRef.current) return;

            clearHover();
            hoveredIdRef.current = ident;
            try {
                mlMap.setFeatureState({ source: DMA_SOURCE_ID, id: ident }, { hover: true });
            } catch {
                // Source not ready yet — ignore; next mousemove retries.
            }
        };

        // Any camera movement clears the hover highlight (same safe { hover: false }
        // path); the isMoving() guard above suppresses recreation until movement ends.
        const handleMoveStart = () => {
            clearHover();
        };

        // Registered once — delegated layer listeners re-resolve their layer ids on every
        // event, so they survive a style reload. Re-registering them on `style.load` would
        // duplicate them: MapLibre dedupes plain listeners but not delegated ones.
        mlMap.on('mousemove', ORBIT_LAYER_IDS.dmaFill, handleMouseMove);
        mlMap.on('mouseleave', ORBIT_LAYER_IDS.dmaFill, clearHover);
        mlMap.on('movestart', handleMoveStart);

        return () => {
            clearHover();
            mlMap.off('mousemove', ORBIT_LAYER_IDS.dmaFill, handleMouseMove);
            mlMap.off('mouseleave', ORBIT_LAYER_IDS.dmaFill, clearHover);
            mlMap.off('movestart', handleMoveStart);
        };
    }, [isMapLoaded, mapRef]);
}
